import React, { useCallback, useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { FieldStateKey, NO_KEY, PermissionKey, YES_KEY } from '@/common/constants';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { FILE_TYPE_TITLES } from '@/modules/FileManager/FileManager.constants';
import { useAdjustmentBalanceFeatureFlag } from '@/modules/FileManager/hooks';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';
import { IFileTypeDtoFileTypeEnum } from '@/types';
import { useFieldsWithDefaultValues } from '@/utils';

import FileLocationField from '../FileLocationField';
import { useStore as useFileManagerSettingsStore } from '../stores/useFileManagerSettings.store';

const REQUIRED_TEXT = 'This field is required';

const Label: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <Box flex={{ grow: 1 }}>
    <Inscription margin={{ bottom: '2px' }} weight="bold" color="textBody">
      {title}
    </Inscription>
    <Box>
      <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
        {desc}
      </Inscription>
    </Box>
  </Box>
);

interface Props {
  isEditMode?: boolean;
  isSubmitted?: boolean;
  isPartnerMode?: boolean;
}

const useFileManagerFields = ({ isEditMode, isSubmitted, isPartnerMode }: Props) => {
  const isFileManagerAccessEnabledPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.IS_FILE_MANAGER_ACCESS_ENABLED,
    FieldStateKey.IS_FILE_MANAGER_ACCESS_ENABLED
  );

  const isFileAutoPostEnabledPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.IS_FILE_AUTO_POST_ENABLED,
    FieldStateKey.IS_FILE_AUTO_POST_ENABLED
  );
  const fileLocationPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.FILE_LOCATION,
    FieldStateKey.FILE_LOCATION
  );
  const isFileManagerAccessEnabled = useFileManagerSettingsStore(
    (state) => state.state.isFileManagerAccessEnabled
  );
  const sourceState = useFileManagerSettingsStore((state) => state.sourceState);
  const isSendFiles = useFileManagerSettingsStore((state) => state.state.isSendFiles);
  const isAutoFileLocation = useFileManagerSettingsStore((state) => state.state.isAutoFileLocation);
  const fileLocation = useFileManagerSettingsStore((state) => state.state.fileLocation);

  const setState = useFileManagerSettingsStore((state) => state.setState);
  const handleCheck = useCallback(
    (value: boolean) => {
      setState({
        isSendFiles: value,
      });
    },
    [setState]
  );

  const handleSetAutoFileLocation = useCallback(
    (value: boolean) => {
      setState({
        isAutoFileLocation: value,
      });
    },
    [setState]
  );
  const handleChangeFileLocation = useCallback(
    (value: string) => {
      setState({
        fileLocation: value,
      });
    },
    [setState]
  );
  const isAdjustmentBalanceAvailable = useAdjustmentBalanceFeatureFlag();
  const fields = useMemo(() => {
    return [
      {
        name: 'isFileManagerAccessEnabled',
        label: 'File manager access',
        type: FieldTypes.Checkbox,
        disabled: !isEditMode,
        value: isFileManagerAccessEnabled,
        subFields: [
          {
            name: 'allowedFileTypes',
            label: <Label title="Allowed file types" desc="At least one option needs to be selected" />,
            type: FieldTypes.CheckBoxGroup,
            showRequireIcon: true,
            disabled: !isEditMode,
            validator: VALIDATORS.ARRAY_REQUIRED.min(1, REQUIRED_TEXT),
            hasHorizontalLine: true,
            options: [
              {
                value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.CENSUS],
                key: IFileTypeDtoFileTypeEnum.CENSUS,
              },
              {
                value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.ENROLLMENT],
                key: IFileTypeDtoFileTypeEnum.ENROLLMENT,
              },
              {
                value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.CONTRIBUTION],
                key: IFileTypeDtoFileTypeEnum.CONTRIBUTION,
              },
              {
                value: 'Carrier claim',
                key: IFileTypeDtoFileTypeEnum.CARRIER,
              },
              {
                value: 'Dependent file',
                key: IFileTypeDtoFileTypeEnum.DEPENDENT,
              },
              ...(isAdjustmentBalanceAvailable
                ? [
                    {
                      value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.BALANCE_ADJUSTMENT],
                      key: IFileTypeDtoFileTypeEnum.BALANCE_ADJUSTMENT,
                    },
                  ]
                : []),
            ],
            ...isFileManagerAccessEnabledPermProps,
          },
          {
            name: 'duplicateCheckFileTypes',
            label: (
              <Label
                title="Duplicate check file types"
                desc="Select file types to be flagged for review if it is identified as a duplicate. At least one file type must be selected."
              />
            ),
            type: FieldTypes.CheckBoxGroup,
            showRequireIcon: false,
            disabled: !isEditMode,
            validator: VALIDATORS.ARRAY,
            options: [
              {
                value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.CENSUS],
                key: IFileTypeDtoFileTypeEnum.CENSUS,
              },
              {
                value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.ENROLLMENT],
                key: IFileTypeDtoFileTypeEnum.ENROLLMENT,
              },
              {
                value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.CONTRIBUTION],
                key: IFileTypeDtoFileTypeEnum.CONTRIBUTION,
              },
              {
                value: 'Dependent file',
                key: IFileTypeDtoFileTypeEnum.DEPENDENT,
              },
              ...(isAdjustmentBalanceAvailable
                ? [
                    {
                      value: FILE_TYPE_TITLES[IFileTypeDtoFileTypeEnum.BALANCE_ADJUSTMENT],
                      key: IFileTypeDtoFileTypeEnum.BALANCE_ADJUSTMENT,
                    },
                  ]
                : []),
            ],
            ...isFileManagerAccessEnabledPermProps,
          },
          {
            name: 'isFileAutoPostEnabled',
            label: (
              <Label
                title="Autopost"
                desc="Allows files to be processed immediately after submission. Otherwise, files must be manually approved for processing"
              />
            ),
            showRequireIcon: true,
            disabled: !isEditMode,
            type: FieldTypes.Radio,
            validator: VALIDATORS.REQUIRED_STRING,

            options: [
              {
                label: 'Yes',
                value: YES_KEY,
              },
              {
                label: 'No',
                value: NO_KEY,
              },
            ],
            ...isFileAutoPostEnabledPermProps,
          },

          {
            name: 'fileLocationCheckbox',
            isDisabled: !isPartnerMode || !isEditMode,
            label: (
              <Label
                title="Send Files (SFTP)"
                desc="Delivery of census, enrollment, contribution, and dependent files. Error reports folder will be created within the same location"
              />
            ),
            value: (
              <FileLocationField
                onChangeCheck={handleCheck}
                onChange={handleChangeFileLocation}
                onSetAutoFileLocation={handleSetAutoFileLocation}
                isAutoFileLocation={isAutoFileLocation}
                checked={isSendFiles}
                isViewMode={!isEditMode}
                disabled={!isPartnerMode || !isEditMode || fileLocationPermProps?.disabled}
                value={fileLocation}
                isError={isSubmitted}
              />
            ),
            disabled: !isEditMode,
            type: FieldTypes.Node,
            placeholder: 'Send Files (SFTP)',
          },
        ],
        ...isFileManagerAccessEnabledPermProps,
      },
    ];
  }, [
    isAdjustmentBalanceAvailable,
    isEditMode,
    isFileManagerAccessEnabledPermProps,
    isFileAutoPostEnabledPermProps,
    isPartnerMode,
    handleCheck,
    handleChangeFileLocation,
    handleSetAutoFileLocation,
    isAutoFileLocation,
    isSendFiles,
    fileLocationPermProps?.disabled,
    fileLocation,
    isSubmitted,
    isFileManagerAccessEnabled,
  ]);

  const fieldsWithValues = useFieldsWithDefaultValues(fields as Field[], sourceState);
  const formattedFields = useHideFields(fieldsWithValues);
  return formattedFields;
};

export default useFileManagerFields;
